// Header.js
import * as React from "react";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import "assets/styles.css";
import { useNavigate } from "react-router-dom";
import { useGender } from "contexts/GenderContext";
import TeamFlagImage from "components/TeamFlagImage/TeamFlagImage";
import SeasonKpiCards from "../SeasonKpiCards/SeasonKpiCards";
import TeamHistoryExportButton from "../TeamHistoryExportButton/TeamHistoryExportButton";
import TeamCompareButton from "pages/teamComparison/components/TeamCompareButton/TeamCompareButton";

const GENDER_OPTIONS = ["Men", "Women"];

export default function Header({
  teamName,
  rank,
  points,
  confederationName,
  confederationCode,
  kpiMetrics,
  onExportClick,
  onCompareClick,
  isExportDisabled,
  isCompareDisabled,
  hideExportButton,
}) {
  const navigate = useNavigate();
  const { selectedGender, setSelectedGender } = useGender();

  const handleGenderSelect = (gender) => {
    if (gender === selectedGender) return;

    setSelectedGender(gender);
    navigate(`/team-details-live/${gender.toLowerCase()}/${teamName}`);
  };

  return (
    <Box className="OuterHeader" data-team-history-export-section="header">
      <Box className="HeaderLeft">
        <TeamFlagImage
          teamName={teamName}
          className="Flag"
          placeholderClassName="TeamFlagPlaceholder--header"
        />
        <Box className="Header1">
          <Box className="TDHeaderTitleRow">
            <div className="Header1Box1Typo1">{teamName}</div>
            <Box className="TDHeaderGenderToggle" role="group" aria-label="Team gender">
              {GENDER_OPTIONS.map((gender) => (
                <button
                  key={gender}
                  type="button"
                  className={`TDHeaderGenderToggleOption${
                    selectedGender === gender ? " active" : ""
                  }`}
                  onClick={() => handleGenderSelect(gender)}
                  aria-pressed={selectedGender === gender}
                >
                  {gender === "Men" ? "MEN" : "WOMEN"}
                </button>
              ))}
            </Box>
            {onCompareClick && (
              <TeamCompareButton
                onClick={onCompareClick}
                disabled={isCompareDisabled}
              />
            )}
            {onExportClick && !hideExportButton && (
              <TeamHistoryExportButton
                onClick={onExportClick}
                disabled={isExportDisabled}
              />
            )}
          </Box>
          <Box className="Header1Box2">
            <Box className="Header1Box2Frame1">
              <div className="Header1Box2Frame1Typo">{`${confederationName}`}</div>
            </Box>
            <Box className="Header1Box2Frame2">
              <div className="Header1Box2Frame2Typo">{`${confederationCode}`}</div>
            </Box>
          </Box>
          <div className="TDSeasonNoteTypo">
            Note: This represents current season info
          </div>
          <SeasonKpiCards metrics={kpiMetrics} variant="header" />
        </Box>
      </Box>
      <Box className="Header2">
        <div className="RankTypo">#{rank}</div>
        <div className="PointsTypo">{points} Points</div>
      </Box>
    </Box>
  );
}

Header.propTypes = {
  teamName: PropTypes.string.isRequired,
  rank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  confederationName: PropTypes.string.isRequired,
  confederationCode: PropTypes.string.isRequired,
  kpiMetrics: PropTypes.shape({
    pointsToNextRank: PropTypes.string,
    pointsToFirst: PropTypes.string,
    winRate: PropTypes.string,
  }),
  onExportClick: PropTypes.func,
  onCompareClick: PropTypes.func,
  isExportDisabled: PropTypes.bool,
  isCompareDisabled: PropTypes.bool,
  hideExportButton: PropTypes.bool,
};

Header.defaultProps = {
  kpiMetrics: {
    pointsToNextRank: "N/A",
    pointsToFirst: "N/A",
    winRate: "N/A",
  },
  onExportClick: undefined,
  onCompareClick: undefined,
  isExportDisabled: false,
  isCompareDisabled: false,
  hideExportButton: false,
};
